/**
 * Money in the admin forms: the owner types currency units ("12,50" or
 * "12.50", euros, złoty, koruna, lei) and the API stores cents. Every admin
 * money field (paket prices, promotion minimum and cap, coupon amounts) goes
 * through these, so they all accept the same input and show the same unit.
 */
import { CURRENCY_MAP_TO_CURRENCY_SYMBOL } from "@configs/currencies";

/**
 * "12,50" / "12.5" / "12" / " 1 200,00 " → cents; "" → null (empty field).
 * At most two decimals: "7,555" is refused, never silently rounded. A
 * thousands separator other than a space is refused too ("1.200,50" is
 * ambiguous), so a mistyped amount never saves as something else.
 */
export function parseMoneyUnits(
  raw: string | number | null | undefined,
): { ok: true; cents: number | null } | { ok: false } {
  const s = String(raw ?? "")
    .replace(/[\s ]/g, "")
    .replace(",", ".");
  if (s === "") return { ok: true, cents: null };
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return { ok: false };
  return { ok: true, cents: Math.round(Number(s) * 100) };
}

/** Cents → the text a form field shows: 1250 → "12,50", 4000 → "40,00", null → "". */
export function centsToUnitsInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(Number(cents)))
    return "";
  return (Number(cents) / 100).toFixed(2).replace(".", ",");
}

/** The unit shown at the end of a money field: "€", "zł", "Kč", "Lei". */
export function currencySuffix(currency: string | null | undefined): string {
  const key = String(currency ?? "eur").toUpperCase() as keyof typeof CURRENCY_MAP_TO_CURRENCY_SYMBOL;
  return CURRENCY_MAP_TO_CURRENCY_SYMBOL[key] ?? key;
}

/** The validation message a money field shows, or null when the input is fine. */
export function moneyFieldError(
  raw: string | number | null | undefined,
  opts: { required?: boolean; positive?: boolean } = {},
): string | null {
  const p = parseMoneyUnits(raw);
  if (!p.ok) return "Vpišite znesek, npr. 12,50.";
  if (p.cents === null) return opts.required ? "Znesek je obvezen." : null;
  if (opts.positive && p.cents <= 0) return "Znesek mora biti večji od 0.";
  return null;
}
